import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, User, Settings, CreditCard, LogOut, HelpCircle } from 'lucide-react';
import theme from '../theme';

const GlobalHeader = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const profileRef = useRef(null);
  const notifRef = useRef(null);

  // Mock notifications
  const notifications = [
    { id: 1, title: 'Portfolio Ready to Launch', message: 'Summer Collection has reached activation target', time: '2h ago', link: '/creator/portfolios' },
    { id: 2, title: 'New Payout Scheduled', message: 'Your quarterly payout of $2,450 is scheduled', time: '1d ago', link: '/creator/payouts' },
    { id: 3, title: 'Contract Quota Update', message: 'You have 3 contracts remaining', time: '2d ago', link: '/creator/billing' },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/${user.role}/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const getInitials = () => {
    if (!user?.name) return 'U';
    return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <header style={{
      background: theme.colors.bg.card,
      borderBottom: `1px solid ${theme.colors.borders}`,
      padding: '0 24px',
      height: '64px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: theme.colors.shadows.sm,
      position: 'sticky',
      top: 0,
      zIndex: 100
    }}>
      {/* Logo */}
      <div 
        onClick={() => navigate(`/${user.role}/dashboard`)}
        style={{
          fontFamily: theme.fonts.heading,
          fontSize: '20px',
          fontWeight: 700,
          color: theme.colors.primary,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <span style={{ color: theme.colors.neon.purple }}>Kickback</span>
        <span style={{ color: theme.colors.text.primary }}>Contracts</span>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearch} style={{ flex: '0 1 500px', margin: '0 32px' }}>
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center'
        }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '12px',
            color: theme.colors.text.muted
          }} />
          <input
            type="text"
            placeholder="Search portfolios, NFTs, utilities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="global-search-input"
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              border: `1px solid ${theme.colors.borders}`,
              borderRadius: theme.radius.md,
              fontSize: '14px',
              color: theme.colors.text.primary,
              background: theme.colors.bg.secondary,
              outline: 'none',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = theme.colors.primary}
            onBlur={(e) => e.target.style.borderColor = theme.colors.borders}
          />
        </div>
      </form>

      {/* Right Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Notifications */}
        <div ref={notifRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            style={{
              position: 'relative',
              padding: '8px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              borderRadius: theme.radius.sm,
              display: 'flex',
              alignItems: 'center',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = theme.colors.bg.secondary}
            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <Bell size={20} style={{ color: theme.colors.text.secondary }} />
            {notifications.length > 0 && (
              <span style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                width: '8px',
                height: '8px',
                background: theme.colors.status.error,
                borderRadius: '50%',
                border: `2px solid ${theme.colors.bg.card}`
              }} />
            )}
          </button>

          {/* Notifications Dropdown */}
          {notificationsOpen && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              width: '360px',
              background: theme.colors.bg.card,
              border: `1px solid ${theme.colors.borders}`,
              borderRadius: theme.radius.md,
              boxShadow: theme.colors.shadows.lg,
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${theme.colors.borders}`,
                fontWeight: 600,
                color: theme.colors.text.primary
              }}>
                Notifications
              </div>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => {
                      navigate(notif.link);
                      setNotificationsOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      borderBottom: `1px solid ${theme.colors.borders}`,
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = theme.colors.bg.secondary}
                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{
                      fontWeight: 600,
                      fontSize: '14px',
                      color: theme.colors.text.primary,
                      marginBottom: '4px'
                    }}>
                      {notif.title}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: theme.colors.text.secondary,
                      marginBottom: '4px'
                    }}>
                      {notif.message}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: theme.colors.text.muted
                    }}>
                      {notif.time}
                    </div>
                  </div>
                ))}
              </div>
              <div
                onClick={() => {
                  navigate(`/${user.role}/notifications`);
                  setNotificationsOpen(false);
                }}
                style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.colors.primary,
                  cursor: 'pointer',
                  borderTop: `1px solid ${theme.colors.borders}`
                }}
              >
                View All Notifications
              </div>
            </div>
          )}
        </div>

        {/* Profile Dropdown */}
        <div ref={profileRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setProfileOpen(!profileOpen)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px 6px 6px',
              border: `1px solid ${theme.colors.borders}`,
              background: theme.colors.bg.card,
              borderRadius: theme.radius.cta,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.borderColor = theme.colors.primary}
            onMouseOut={(e) => e.currentTarget.style.borderColor = theme.colors.borders}
          >
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.colors.neon.purple}, ${theme.colors.neon.blue})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.colors.text.inverse,
              fontSize: '12px',
              fontWeight: 700
            }}>
              {getInitials()}
            </div>
            <span style={{
              fontSize: '14px',
              fontWeight: 500,
              color: theme.colors.text.primary
            }}>
              {user?.name || 'User'}
            </span>
          </button>

          {/* Profile Dropdown Menu */}
          {profileOpen && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              width: '220px',
              background: theme.colors.bg.card,
              border: `1px solid ${theme.colors.borders}`,
              borderRadius: theme.radius.md,
              boxShadow: theme.colors.shadows.lg,
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${theme.colors.borders}`
              }}>
                <div style={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: theme.colors.text.primary
                }}>
                  {user?.name}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: theme.colors.text.muted,
                  marginTop: '2px'
                }}>
                  {user?.email}
                </div>
              </div>

              {[
                { icon: User, label: 'Profile / Account', path: `/${user.role}/settings` },
                { icon: CreditCard, label: 'Billing', path: `/${user.role}/billing` },
                { icon: Settings, label: 'Settings', path: `/${user.role}/settings` },
                { icon: HelpCircle, label: 'Help', path: '/help' },
              ].map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    navigate(item.path);
                    setProfileOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    fontSize: '14px',
                    color: theme.colors.text.primary
                  }}
                  onMouseOver={(e) => e.currentTarget.style.background = theme.colors.bg.secondary}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <item.icon size={16} style={{ color: theme.colors.text.muted }} />
                  {item.label}
                </div>
              ))}

              <div
                onClick={() => {
                  onLogout();
                  setProfileOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  borderTop: `1px solid ${theme.colors.borders}`,
                  fontSize: '14px',
                  color: theme.colors.status.error,
                  fontWeight: 600
                }}
                onMouseOver={(e) => e.currentTarget.style.background = theme.colors.bg.secondary}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <LogOut size={16} />
                Log Out
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default GlobalHeader;
